import React, { useCallback, useEffect, useMemo, useRef } from "react";

import { Form, Formik } from "formik";
import styled from "styled-components/macro";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";

import {
  resetSideLoadedDataSetMappingError,
  saveSideLoadedDataSetMapping,
} from "actions/sideLoadedData";

import { ColumnMappingGrid } from "components/Business/SideLoadedBusinesses/ColumnMappingGrid";
import { useSideLoadedFieldConfigurations } from "components/Business/SideLoadedBusinesses/useSideLoadedFieldConfigurations";
import ErrorMessage from "components/ErrorMessage";
import { Button, SecondaryButton } from "components/Form";
import { Input, SelectField } from "components/Form/FormikControls";
import { Column } from "components/Layout";
import {
  DialogActions,
  DialogContent,
  DialogTitle,
} from "components/MaterialDialog";
import { SmallText } from "components/Text";

import { SIDE_LOADED_PREVIEW_ROW_COUNT } from "constants/sideLoadedBusinesses";

import {
  entriesForColumns,
  entriesForTemplate,
  hasDoubledUpTargets,
  mappedColumnsSummary,
  mappingEntriesMatch,
  slotOverflow,
} from "lib/sideLoadedFieldMapping";

import {
  getIsSavingSideLoadedDataSetMapping,
  getSideLoadedDataSetMappingError,
  selectSideLoadedBusinessesByDatasetId,
} from "selectors";

import { TemplateEditWarning } from "./TemplateEditWarning";
import { NEW_TEMPLATE, useTemplateOptions } from "./useTemplateOptions";

// flex: 1 so the message pushes the dialog's buttons to the right.
const MappingError = styled(ErrorMessage)`
  flex: 1;
`;

interface MappingStepProps {
  dataSet: SideLoadedDataSet;
  onDone: () => void;
}

export function MappingStep({
  dataSet,
  onDone,
}: MappingStepProps): React.JSX.Element {
  const dispatch = useDispatch();
  const records = useSelector(
    selectSideLoadedBusinessesByDatasetId(dataSet.id),
  );

  const { userRoleContentTypeName, configurations } =
    useSideLoadedFieldConfigurations();

  const previewRows = useMemo(
    () =>
      records
        .slice(0, SIDE_LOADED_PREVIEW_ROW_COUNT)
        .map(record => record.rawData),
    [records],
  );

  // Data sets imported before templates existed have no source_columns, which
  // would leave the grid with no columns to map.
  const sourceColumns = useMemo(
    () =>
      dataSet.sourceColumns?.length
        ? dataSet.sourceColumns
        : Object.keys(records[0]?.rawData || {}),
    [dataSet.sourceColumns, records],
  );

  const {
    templates: mappings,
    templateOptions: mappingOptions,
    autoMatchedId,
    dataSetCountByTemplateId: dataSetCountByMappingId,
  } = useTemplateOptions(sourceColumns);

  const initialValues = useMemo(() => {
    // Editing has to start from the template in force, not one that merely matches.
    const linkedOrRecommendedId = dataSet.mappingId || autoMatchedId;
    const existing = mappings.find(
      mapping => mapping.id === linkedOrRecommendedId,
    );
    if (existing) {
      return {
        mappingId: existing.id,
        name: existing.name,
        entries: entriesForTemplate(existing, sourceColumns, configurations),
      };
    }

    return {
      mappingId: NEW_TEMPLATE,
      name: dataSet.name,
      entries: entriesForTemplate(undefined, sourceColumns, configurations),
    };
  }, [autoMatchedId, configurations, dataSet, mappings, sourceColumns]);

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        name: Yup.string().required("Give the template a name"),
        entries: Yup.array()
          .test(
            "targets-are-not-doubled-up",
            "Each AgriNous field can only be mapped once",
            entries => !hasDoubledUpTargets(entries, configurations),
          )
          .test("targets-fit-their-slots", "", function fitsSlots(entries) {
            const overflow = slotOverflow(entries, configurations);
            return overflow
              ? this.createError({
                  message: `At most ${overflow.slotCount} columns can be mapped to ${overflow.label}`,
                })
              : true;
          }),
      }),
    [configurations],
  );

  const isSaving = useSelector(getIsSavingSideLoadedDataSetMapping);
  const saveError = useSelector(getSideLoadedDataSetMappingError);

  // A previous attempt's error is stored, not local, so clear it on open.
  useEffect(() => {
    dispatch(resetSideLoadedDataSetMappingError());
  }, [dispatch]);

  // Left open until the server answers, so a refusal can be shown here.
  const wasSaving = useRef(false);
  useEffect(() => {
    if (wasSaving.current && !isSaving && !saveError) {
      onDone();
    }
    wasSaving.current = isSaving;
  }, [isSaving, saveError, onDone]);

  const handleSubmit = useCallback(
    values => {
      dispatch(
        saveSideLoadedDataSetMapping({
          dataSetId: dataSet.id,
          mappingId: values.mappingId || null,
          name: values.name,
          // Filtered to what the grid actually offered: a template authored
          // elsewhere can name a column this data set has no record of.
          entries: entriesForColumns(values.entries, sourceColumns),
          // Sent so the template's columns cannot outlive the entries mapping
          // them - this data set's file is what was just mapped.
          sourceColumns,
        }),
      );
    },
    [dispatch, dataSet.id, sourceColumns],
  );

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ values, setValues, setFieldValue, errors, submitCount }) => {
        const selectedMapping = mappings.find(
          mapping => mapping.id === values.mappingId,
        );
        const hasEdits =
          Boolean(selectedMapping) &&
          !mappingEntriesMatch(
            selectedMapping.entries,
            values.entries,
            sourceColumns,
          );

        // This data set already exists, so it counts towards its own mapping's
        // usage and has to come out of an "other" count.
        const otherDataSetCount = Math.max(
          (dataSetCountByMappingId[selectedMapping?.id] || 0) -
            (selectedMapping?.id === dataSet.mappingId ? 1 : 0),
          0,
        );

        return (
          <Form>
            <DialogTitle onClose={onDone}>
              Edit Column Template for {dataSet.name}
            </DialogTitle>

            <DialogContent dividers>
              <Column gridGap={2}>
                <SmallText>
                  {mappedColumnsSummary(
                    values.entries.length,
                    sourceColumns.length,
                  )}
                </SmallText>

                {mappings.length > 0 && (
                  <SelectField
                    label="Column Template"
                    tooltip="Reuses a saved column mapping, or creates a new one from your choices below."
                    name="mappingId"
                    options={mappingOptions}
                    onChangeExtra={mappingId => {
                      const chosen = mappings.find(
                        mapping => mapping.id === mappingId,
                      );
                      setValues({
                        ...values,
                        mappingId: mappingId || NEW_TEMPLATE,
                        name: chosen ? chosen.name : dataSet.name,
                        entries: entriesForTemplate(
                          chosen,
                          sourceColumns,
                          configurations,
                        ),
                      });
                    }}
                  />
                )}

                <Input
                  label="Template Name"
                  name="name"
                  dataTour="sideLoadedMappingTemplateName"
                />

                {selectedMapping && hasEdits && (
                  <TemplateEditWarning
                    templateName={selectedMapping.name}
                    otherDataSetCount={otherDataSetCount}
                  />
                )}

                <ColumnMappingGrid
                  sourceColumns={sourceColumns}
                  rows={previewRows}
                  totalRowCount={records.length}
                  entries={values.entries}
                  onChange={entries => setFieldValue("entries", entries)}
                  userRoleContentTypeName={userRoleContentTypeName}
                />
              </Column>
            </DialogContent>

            <DialogActions>
              {submitCount > 0 && (errors.entries || errors.name) ? (
                <MappingError>{errors.entries || errors.name}</MappingError>
              ) : (
                saveError && <MappingError>{saveError}</MappingError>
              )}
              <SecondaryButton
                type="button"
                data-tour="backFromSideLoadedColumnMapping"
                onClick={onDone}
              >
                Back
              </SecondaryButton>
              <Button
                type="submit"
                data-tour="saveSideLoadedColumnTemplate"
                disabled={isSaving}
              >
                Save Template
              </Button>
            </DialogActions>
          </Form>
        );
      }}
    </Formik>
  );
}
